import React, { useEffect, useMemo, useState } from 'react';
import { SearchBar, Spacer } from '@tw/tw-component-library';
import { Button } from '@/ui';
import {
  FiBriefcase,
  FiChevronLeft,
  FiChevronRight,
  FiLock,
  FiMessageSquare,
  FiPlay,
  FiSearch,
  FiUpload,
  FiUsers,
} from 'react-icons/fi';
import {
  WorkspaceGroup,
  WorkspaceItem,
} from '@/contexts/WorkspaceContext';
import {
  ActivityIcon,
  ActivityItemMain,
  ActivityItemMeta,
  ActivityItemTitle,
  ActivityLeft,
  ActivityList,
  ActivityRow,
  ActivityTimestamp,
  ActivityTitle,
  ActivityTitleRow,
  CompactActionButton,
  Container,
  EmptyState,
  GroupFilterLabel,
  GroupFilterRow,
  GroupFilters,
  GroupTab,
  GroupTabCount,
  ItemQuickActions,
  ListLessLabel,
  ListLessMeta,
  ListLessRow,
  PaginationControls,
  PaginationInfo,
} from './styles';

export type CompactWorkspaceDirectoryListProps = {
  title: string;
  groups: WorkspaceGroup[];
  listLessTab?: string;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  enablePagination?: boolean;
  itemsPerPage?: number;
  enableTabs?: boolean;
  onItemClick?: (item: WorkspaceItem) => void;
  onCreateAskSession?: (item: WorkspaceItem) => void;
  onUploadDocument?: (item: WorkspaceItem) => void;
  onRunTask?: (item: WorkspaceItem) => void;
  className?: string;
};

const filterItems = (items: WorkspaceItem[], searchTerm: string) => {
  if (!searchTerm.trim()) return items;
  const lowerSearch = searchTerm.toLowerCase();
  return items.filter(
    (item) =>
      item.title.toLowerCase().includes(lowerSearch) ||
      item.lastUsed.toLowerCase().includes(lowerSearch),
  );
};

const getActivityIcon = (item: WorkspaceItem) => {
  if (item.workspaceType === 'Private') return <FiLock />;
  if (item.workspaceType === 'Collaborative') return <FiUsers />;
  return <FiBriefcase />;
};

/** Same meta line as WorkspaceList activity variant. */
const buildActivityMeta = (item: WorkspaceItem): string => {
  const parts: string[] = [];
  if (item.workspaceType) {
    parts.push(item.workspaceType);
  }
  if (typeof item.documentCount === 'number') {
    parts.push(
      `${item.documentCount} file${item.documentCount === 1 ? '' : 's'}`,
    );
  }
  if (typeof item.askSessionCount === 'number') {
    parts.push(
      `${item.askSessionCount} ask session${item.askSessionCount === 1 ? '' : 's'}`,
    );
  }
  if (typeof item.documentsLinkedToAsksCount === 'number') {
    parts.push(`${item.documentsLinkedToAsksCount} linked`);
  }
  return parts.join(' · ') || 'Open workspace';
};

export const CompactWorkspaceDirectoryList: React.FC<
  CompactWorkspaceDirectoryListProps
> = ({
  title,
  groups,
  listLessTab,
  enableSearch = false,
  searchPlaceholder = 'Search workspaces...',
  enablePagination = false,
  itemsPerPage = 10,
  enableTabs = false,
  onItemClick,
  onCreateAskSession,
  onUploadDocument,
  onRunTask,
  className,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedTabIndex]);

  useEffect(() => {
    if (enableTabs && groups.length > 0) {
      if (listLessTab && groups[0]?.title === listLessTab && groups.length > 1) {
        setSelectedTabIndex(1);
      } else {
        setSelectedTabIndex(0);
      }
    }
  }, [enableTabs, groups, listLessTab]);

  const listLessGroup = useMemo(() => {
    if (!listLessTab) return null;
    return groups.find((group) => group.title === listLessTab) ?? null;
  }, [listLessTab, groups]);

  const groupsWithoutListLess = useMemo(() => {
    if (!listLessGroup) return groups;
    return groups.filter((group) => group.title !== listLessTab);
  }, [groups, listLessGroup, listLessTab]);

  const filteredGroups = useMemo(() => {
    const groupsToUse = groupsWithoutListLess;
    let groupsToFilter = groupsToUse;

    if (enableTabs && groupsToUse.length > 0) {
      const selectedGroup = groups[selectedTabIndex];
      if (selectedGroup && selectedGroup.title !== listLessTab) {
        const filteredIndex = groupsToUse.findIndex(
          (g) => g.title === selectedGroup.title,
        );
        groupsToFilter =
          filteredIndex >= 0 ? [groupsToUse[filteredIndex]] : [groupsToUse[0]];
      } else if (selectedGroup?.title === listLessTab) {
        return undefined;
      } else {
        groupsToFilter = [groupsToUse[0]];
      }
    }

    if (!searchTerm.trim()) return groupsToFilter;

    const filtered = groupsToFilter
      .map((group) => ({
        ...group,
        items: filterItems(group.items, searchTerm),
      }))
      .filter((group) => group.items.length > 0);

    return filtered.length > 0 ? filtered : undefined;
  }, [
    groups,
    groupsWithoutListLess,
    searchTerm,
    enableTabs,
    selectedTabIndex,
    listLessTab,
  ]);

  const allItemsToDisplay = useMemo(
    () => filteredGroups?.flatMap((group) => group.items) ?? [],
    [filteredGroups],
  );

  const activityItems = useMemo(() => {
    if (!enablePagination || allItemsToDisplay.length <= itemsPerPage) {
      return allItemsToDisplay;
    }
    const startIndex = currentPage * itemsPerPage;
    return allItemsToDisplay.slice(startIndex, startIndex + itemsPerPage);
  }, [enablePagination, allItemsToDisplay, itemsPerPage, currentPage]);

  const totalPages = useMemo(() => {
    if (!enablePagination || allItemsToDisplay.length <= itemsPerPage) {
      return 1;
    }
    return Math.ceil(allItemsToDisplay.length / itemsPerPage);
  }, [enablePagination, allItemsToDisplay.length, itemsPerPage]);

  const hasResults = allItemsToDisplay.length > 0;
  const showPagination =
    enablePagination && allItemsToDisplay.length > itemsPerPage;

  const getTabItemCount = (groupTitle: string) => {
    if (searchTerm.trim()) {
      return (
        filteredGroups?.find((g) => g.title === groupTitle)?.items.length ?? 0
      );
    }
    return groups.find((g) => g.title === groupTitle)?.items.length ?? 0;
  };

  const renderItem = (item: WorkspaceItem) => (
    <ActivityRow key={item.id}>
      <ActivityLeft type="button" onClick={() => onItemClick?.(item)}>
        <ActivityIcon>{getActivityIcon(item)}</ActivityIcon>
        <ActivityItemMain>
          <ActivityTitleRow>
            <ActivityItemTitle>{item.title}</ActivityItemTitle>
            <ActivityTimestamp>{item.lastUsed}</ActivityTimestamp>
          </ActivityTitleRow>
          <ActivityItemMeta>{buildActivityMeta(item)}</ActivityItemMeta>
        </ActivityItemMain>
      </ActivityLeft>
      <ItemQuickActions
        role="group"
        aria-label={`Quick actions for ${item.title}`}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {onCreateAskSession && (
          <CompactActionButton
            type="button"
            size="sm"
            variant="ghost"
            leftIcon={<FiMessageSquare />}
            onClick={() => onCreateAskSession(item)}
          >
            Ask session
          </CompactActionButton>
        )}
        {onUploadDocument && (
          <CompactActionButton
            type="button"
            size="sm"
            variant="ghost"
            leftIcon={<FiUpload />}
            onClick={() => onUploadDocument(item)}
          >
            Upload
          </CompactActionButton>
        )}
        {onRunTask && (
          <CompactActionButton
            type="button"
            size="sm"
            variant="ghost"
            leftIcon={<FiPlay />}
            onClick={() => onRunTask(item)}
          >
            Run task
          </CompactActionButton>
        )}
      </ItemQuickActions>
    </ActivityRow>
  );

  return (
    <Container className={className}>
      <ActivityTitle>{title}</ActivityTitle>

      {enableSearch && (
        <>
          <SearchBar
            searchTerm={searchTerm}
            placeholder={searchPlaceholder}
            autoComplete="off"
            spellCheck={false}
            setSearchTerm={setSearchTerm}
            leftIcon={<FiSearch />}
          />
          <Spacer />
        </>
      )}

      {(listLessGroup?.items.length || groupsWithoutListLess.length > 0) && (
        <GroupFilters>
          {listLessGroup && listLessGroup.items.length > 0 && (
            <ListLessRow
              type="button"
              onClick={() => onItemClick?.(listLessGroup.items[0])}
            >
              <ActivityIcon>
                <FiLock />
              </ActivityIcon>
              <ListLessLabel>{listLessGroup.title}</ListLessLabel>
              <ListLessMeta>
                {listLessGroup.items.length} workspace
                {listLessGroup.items.length !== 1 ? 's' : ''}
              </ListLessMeta>
              <FiChevronRight size={14} aria-hidden />
            </ListLessRow>
          )}

          {enableTabs && groupsWithoutListLess.length > 0 && (
            <GroupFilterRow role="tablist" aria-label="Workspace groups">
              <GroupFilterLabel>Groups</GroupFilterLabel>
              {groupsWithoutListLess.map((group, index) => {
                const originalIndex =
                  groups.findIndex((g) => g.title === group.title) ?? index;
                const isActive = selectedTabIndex === originalIndex;
                const count = getTabItemCount(group.title);
                return (
                  <GroupTab
                    key={group.title ?? index}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    $isActive={isActive}
                    onClick={() => setSelectedTabIndex(originalIndex)}
                    title={group.title}
                  >
                    {group.title}
                    {count > 0 && <GroupTabCount>{count}</GroupTabCount>}
                  </GroupTab>
                );
              })}
            </GroupFilterRow>
          )}
        </GroupFilters>
      )}

      {activityItems.length > 0 ? (
        <ActivityList>{activityItems.map((item) => renderItem(item))}</ActivityList>
      ) : (
        !hasResults && <EmptyState>No workspaces found</EmptyState>
      )}

      {showPagination && (
        <PaginationControls>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
            disabled={currentPage === 0}
            leftIcon={<FiChevronLeft />}
          />
          <PaginationInfo>
            Page {currentPage + 1} of {totalPages}
          </PaginationInfo>
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
            }
            disabled={currentPage >= totalPages - 1}
            rightIcon={<FiChevronRight />}
          />
        </PaginationControls>
      )}
    </Container>
  );
};
